import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Mic, MicOff, Info, ArrowRight, Brain } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Logo from '@/components/Logo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import RoleDetailsStep from '@/components/practice/RoleDetailsStep';
import SkillsSelectionStep from '@/components/practice/SkillsSelectionStep';
import CandidateInfoStep from '@/components/practice/CandidateInfoStep';
import PracticeInterview from '@/components/practice/PracticeInterview';
import { useAuth } from '@/components/auth/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import AIProcessingAnimation from '@/components/assessment/AIProcessingAnimation';
import { supabase } from '@/integrations/supabase/client';

export type PracticeData = {
  role: string;
  level: 'entry' | 'mid' | 'senior';
  selectedSkills: string[];
  candidateName?: string;
  candidateEmail?: string;
};

const InterviewPractice: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [practiceData, setPracticeData] = useState<PracticeData>({
    role: '',
    level: 'mid',
    selectedSkills: [],
  });
  const [showAIProcessing, setShowAIProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState({ title: '', description: '' });
  const [practiceId, setPracticeId] = useState<string | null>(null);
  
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleRoleDetailsSubmit = (role: string, level: 'entry' | 'mid' | 'senior') => {
    setPracticeData((prev) => ({ ...prev, role, level }));
    
    setProcessingMessage({
      title: 'Analyzing Role Requirements',
      description: `Our AI is researching the top skills for ${level} ${role} positions...`
    });
    setShowAIProcessing(true);
    
    setTimeout(() => {
      setShowAIProcessing(false);
      handleNextStep();
    }, 2500);
  };

  const handleSkillsSubmit = async (skills: string[]) => {
    setPracticeData((prev) => ({ ...prev, selectedSkills: skills }));
    handleNextStep();
  };

  const handleCandidateInfoSubmit = async (name: string, email: string) => {
    setPracticeData((prev) => ({ ...prev, candidateName: name, candidateEmail: email }));
    
    // If user is logged in, save the practice session
    if (user) {
      try {
        const { data, error } = await supabase
          .from('practice_interviews')
          .insert([
            {
              user_id: user.id,
              role: practiceData.role,
              level: practiceData.level,
              skills: practiceData.selectedSkills,
              candidate_name: name,
              candidate_email: email,
              created_at: new Date().toISOString(),
            }
          ])
          .select()
          .single();
          
        if (error) throw error;
        
        if (data) {
          setPracticeId(data.id);
        }
      } catch (error) {
        console.error('Error saving practice interview:', error);
        // Continue without saving if there's an error
      }
    }
    
    handleNextStep();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <RoleDetailsStep
            onSubmit={handleRoleDetailsSubmit}
            initialData={practiceData}
          />
        );
      case 2:
        return (
          <SkillsSelectionStep
            onSubmit={handleSkillsSubmit}
            onBack={handleBack}
            initialSkills={practiceData.selectedSkills}
            role={practiceData.role}
            level={practiceData.level}
          />
        );
      case 3:
        return (
          <CandidateInfoStep
            onSubmit={handleCandidateInfoSubmit}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PracticeInterview
            practiceData={practiceData}
            practiceId={practiceId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fafafa] to-[#f3f3ff] flex flex-col">
      {/* Use the shared Header component */}
      <Header />

      {/* Practice Mode indicator */}
      <div className="bg-purple-100 text-purple-800 text-center py-2">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-2">
            <Brain size={16} className="flex-shrink-0" />
            <span className="font-medium">Practice Mode</span>
            <span className="text-sm">- Interview with our AI interviewer, no login required</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center py-8 px-4 sm:px-6 relative">
        <AnimatePresence mode="wait">
          {showAIProcessing ? (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-3xl"
            >
              <AIProcessingAnimation 
                title={processingMessage.title}
                description={processingMessage.description}
              />
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full flex flex-col items-center"
            >
              {currentStep < 4 && (
                <div className="w-full max-w-3xl mx-auto mb-6">
                  <div className="flex items-center justify-between px-2">
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-[#1A1F2C]/70 hover:text-[#1A1F2C] hover:bg-transparent p-0"
                      onClick={handleBack}
                    >
                      <ArrowLeft size={16} />
                      <span>Back</span>
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-8 rounded-full ${currentStep >= 1 ? 'bg-[#9b87f5]' : 'bg-gray-200'}`}></div>
                      <div className={`h-2 w-8 rounded-full ${currentStep >= 2 ? 'bg-[#9b87f5]' : 'bg-gray-200'}`}></div>
                      <div className={`h-2 w-8 rounded-full ${currentStep >= 3 ? 'bg-[#9b87f5]' : 'bg-gray-200'}`}></div>
                      <div className={`h-2 w-8 rounded-full ${currentStep >= 4 ? 'bg-[#9b87f5]' : 'bg-gray-200'}`}></div>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full max-w-3xl mx-auto">
                {renderStep()}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default InterviewPractice; 